import { useEffect, useRef, type RefObject } from 'react';

const TABBABLE = [
  'a[href]', 'area[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', 'iframe', '[contenteditable="true"]', '[tabindex]',
].join(',');
const FIRST_FIELD = 'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])';

// Browsers that know checkVisibility skip hidden elements; jsdom does not
// implement it, so everything counts there.
function visible(el: HTMLElement): boolean {
  const check = (el as HTMLElement & { checkVisibility?: () => boolean }).checkVisibility;
  return typeof check === 'function' ? check.call(el) : true;
}

export function tabbables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(TABBABLE))
    .filter((el) => el.tabIndex >= 0 && !el.closest('[inert]') && visible(el));
}

// Focus trap for a dialog panel.
//  - Opener: read during the render that activates the trap (an autoFocus
//    field inside has already taken focus by the time any effect runs).
//  - Open: focus stays where the content put it (autoFocus); else the first
//    [data-autofocus]; else the first field or button not matching `skip`
//    (the header's Close); else the panel itself.
//  - Tab / Shift+Tab wrap inside the panel. The listener sits ON the panel, so
//    keys typed in a list portaled outside it (a Select, a record picker, a
//    date, a column filter) never reach it: those lists keep their own keys.
//  - Focus that lands on the page behind (#root by default) is pulled back in;
//    focus in any other portal (a picker list, a confirm opened on top) is left
//    alone. Only the top dialog (`isTop`) does any of this.
//  - Close: focus goes back to the opener if it is still on the page and focus
//    has fallen to <body> or into the gone panel (never stolen from elsewhere).
// Panels of the traps now active, oldest first: where focus falls back to when
// the opener is gone and another dialog is still open underneath.
const activePanels: HTMLElement[] = [];

export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  opts: { active: boolean; isTop?: () => boolean; skip?: string; background?: () => HTMLElement | null },
): void {
  const { active } = opts;
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const openerRef = useRef<HTMLElement | null>(null);
  // Captured in render (not an effect) so it sees focus before an autoFocus
  // field takes it; StrictMode's double effect run is harmless, it re-reads this.
  const wasActive = useRef(false);
  if (active && !wasActive.current && typeof document !== 'undefined') {
    openerRef.current = document.activeElement as HTMLElement | null;
  }
  wasActive.current = active;

  useEffect(() => {
    if (!active) return;
    const panel = ref.current;
    if (!panel) return;
    const opener = openerRef.current;
    const isTop = () => (optsRef.current.isTop ? optsRef.current.isTop() : true);

    if (!panel.contains(document.activeElement)) {
      const skip = optsRef.current.skip;
      const marked = panel.querySelector<HTMLElement>('[data-autofocus]');
      const first = marked ?? Array.from(panel.querySelectorAll<HTMLElement>(FIRST_FIELD))
        .find((el) => !(skip && el.matches(skip)) && el.tabIndex >= 0 && visible(el));
      (first ?? panel).focus();
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || e.defaultPrevented || !isTop()) return;
      const list = tabbables(panel);
      if (list.length === 0) { e.preventDefault(); panel.focus(); return; }
      const first = list[0];
      const last = list[list.length - 1];
      const now = document.activeElement;
      if (e.shiftKey && (now === first || now === panel)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && now === last) { e.preventDefault(); first.focus(); }
    };
    const onFocusIn = (e: FocusEvent) => {
      if (!isTop()) return;
      const bg = optsRef.current.background ? optsRef.current.background() : document.getElementById('root');
      const t = e.target as Node | null;
      if (!bg || !t || !bg.contains(t) || panel.contains(t)) return;
      // Another modal overlay on the page (the Help Center) owns its own focus.
      if ((t as Element).closest?.('[role="dialog"][aria-modal="true"]')) return;
      // The first tabbable that is not the header's Close, as on open.
      (tabbables(panel).find((el) => !el.matches('[data-modal-close]')) ?? panel).focus();
    };
    activePanels.push(panel);
    panel.addEventListener('keydown', onKey);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      panel.removeEventListener('keydown', onKey);
      document.removeEventListener('focusin', onFocusIn);
      const i = activePanels.indexOf(panel);
      if (i >= 0) activePanels.splice(i, 1);
      const now = document.activeElement;
      const lost = !now || now === document.body || panel.contains(now);
      if (!lost) return;
      if (opener && opener.isConnected && opener !== document.body) { opener.focus(); return; }
      // No usable opener: hand focus to the newest dialog still open.
      const under = activePanels[activePanels.length - 1];
      if (under) (tabbables(under).find((el) => !el.matches('[data-modal-close]')) ?? under).focus();
    };
    // `ref` is stable; the trap is per opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}
